import { Room } from 'livekit-client'

export interface PermissionsLiveKitOptions {
  permissions: {
    setDeniedMic: (v: boolean) => void
    setDeniedCam: (v: boolean) => void
    setBlocked: (v: boolean) => void
  }
  analytics: {
    permissionsPopUp: (p: { micStatus: string, camStatus: string }) => void
  }
}

interface PermissionsLiveKitReturn {
  requestPermissions: () => Promise<void>
}

const hasGrantedDevice = async (kind: MediaDeviceKind): Promise<boolean> => {
  const devices = await Room.getLocalDevices(kind, false)
  return devices.some((device) => device.label !== '')
}

export const usePermissionsLiveKit = (options: PermissionsLiveKitOptions): PermissionsLiveKitReturn => {
  let pending: Promise<void> | null = null

  const reportDenied = async (): Promise<void> => {
    const [micGranted, camGranted] = await Promise.all([
      hasGrantedDevice('audioinput'),
      hasGrantedDevice('videoinput'),
    ])

    if (!micGranted) options.permissions.setDeniedMic(true)
    if (!camGranted) options.permissions.setDeniedCam(true)
    options.permissions.setBlocked(true)

    options.analytics.permissionsPopUp({
      micStatus: micGranted ? 'Allowed' : 'Blocked',
      camStatus: camGranted ? 'Allowed' : 'Blocked',
    })
  }

  const probe = async (): Promise<void> => {
    try {
      await Room.getLocalDevices(undefined, true)
    } catch (error) {
      if (error instanceof Error && error.name === 'NotAllowedError') {
        try {
          await reportDenied()
        } catch (reportError) {
          console.error('[classroom] permissions denial report failed', reportError)
        }
        return
      }
      console.error('[classroom] permissions request failed', error)
    }
  }

  const requestPermissions = (): Promise<void> => {
    if (!pending) {
      pending = probe().finally(() => {
        pending = null
      })
    }

    return pending
  }

  return { requestPermissions }
}
